import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import {
  ArrowLeft, Calendar, MapPin, Receipt, Package, Truck, Home,
  Loader2, Download, Mail, Clock, CheckCircle2, XCircle, AlertCircle, CreditCard, RotateCcw,
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_META = {
  pending_payment: { label: 'En attente de paiement', color: 'bg-yellow-100 text-yellow-800', dot: '#EAB308', icon: CreditCard },
  payment_failed: { label: 'Paiement échoué', color: 'bg-red-100 text-red-700', dot: '#EF4444', icon: XCircle },
  payment_expired: { label: 'Paiement expiré', color: 'bg-gray-200 text-gray-700', dot: '#94A3B8', icon: Clock },
  payment_canceled: { label: 'Paiement annulé', color: 'bg-gray-200 text-gray-700', dot: '#94A3B8', icon: XCircle },
  pending: { label: 'En attente', color: 'bg-yellow-100 text-yellow-800', dot: '#EAB308', icon: Clock },
  confirmed: { label: 'Confirmée', color: 'bg-green-100 text-green-700', dot: '#10B981', icon: CheckCircle2 },
  in_delivery: { label: 'En livraison', color: 'bg-blue-100 text-blue-700', dot: '#3B82F6', icon: Truck },
  delivered: { label: 'Livrée', color: 'bg-emerald-100 text-emerald-700', dot: '#059669', icon: Home },
  cancelled: { label: 'Annulée', color: 'bg-red-100 text-red-700', dot: '#EF4444', icon: XCircle },
};

const TIMELINE_STEPS = [
  { key: 'confirmed', label: 'Commande confirmée', desc: 'Paiement reçu, facture générée.', icon: Receipt },
  { key: 'in_delivery', label: 'En cours de livraison', desc: 'Votre linge est en route vers votre logement.', icon: Truck },
  { key: 'delivered', label: 'Livré', desc: 'Le linge est dans votre logement.', icon: Home },
];

const statusOrder = ['pending_payment', 'confirmed', 'in_delivery', 'delivered'];
const isStatusReached = (target, current) => {
  const ti = statusOrder.indexOf(target);
  const ci = statusOrder.indexOf(current);
  return ti >= 0 && ci >= ti;
};

const meta = (status) => STATUS_META[status] || STATUS_META.pending_payment;

const OrderDetailPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { user, token, loading: authLoading } = useAuth();
  const { toast } = useToast();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const fetchOrder = useCallback(async () => {
    if (!token || !orderId) return;
    try {
      const res = await axios.get(`${API}/orders/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOrder(res.data);
    } catch (e) {
      toast({
        title: 'Commande introuvable',
        description: 'Cette commande n\'existe pas ou ne vous appartient pas.',
        variant: 'destructive',
      });
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  }, [token, orderId, toast, navigate]);

  // Manual sync trigger for stuck orders (e.g., after Mollie redirect that
  // didn't finalize because of a stale webhook URL on a previous deployment).
  const handleSyncPayment = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(
        `${API}/payments/mollie/sync/${orderId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const newStatus = res.data?.status;
      if (newStatus === 'confirmed') {
        toast({
          title: 'Commande confirmée',
          description: `Paiement validé. Facture n° ${res.data.invoice_number || '—'}.`,
        });
      } else if (['payment_failed', 'payment_expired', 'payment_canceled'].includes(newStatus)) {
        toast({
          title: 'Paiement non finalisé',
          description: 'Aucune somme n\'a été débitée. Vous pouvez recommencer.',
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Toujours en attente',
          description: 'Mollie n\'a pas encore finalisé la transaction. Réessayez dans quelques instants.',
        });
      }
      await fetchOrder();
    } catch (e) {
      toast({
        title: 'Erreur',
        description: 'Impossible de vérifier le statut du paiement pour le moment.',
        variant: 'destructive',
      });
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    if (!authLoading && !user) {
      navigate(`/login?redirect=/dashboard/orders/${orderId}`);
      return;
    }
    if (user) fetchOrder();
  }, [user, authLoading, navigate, orderId, fetchOrder]);

  const handleDownloadInvoice = async () => {
    if (!order) return;
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/orders/${order.id}/invoice`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const invNum = order.invoice_number || order.id.slice(0, 8).toUpperCase();
      link.href = url;
      link.download = `facture-terreetmer-${invNum}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast({ title: 'Facture téléchargée', description: `Facture n° ${invNum}.` });
    } catch (e) {
      toast({
        title: 'Téléchargement impossible',
        description: e?.response?.status === 409
          ? 'La facture sera disponible une fois le paiement confirmé.'
          : 'Réessayez plus tard.',
        variant: 'destructive',
      });
    } finally {
      setDownloading(false);
    }
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#E8945A]" />
      </div>
    );
  }

  if (!order) return null;

  const m = meta(order.status);
  const StatusIcon = m.icon;
  const totalItems = (order.items || []).reduce((s, it) => s + it.quantity, 0);
  const cancelled = ['cancelled', 'payment_failed', 'payment_expired', 'payment_canceled'].includes(order.status);
  const itemsSubtotal = (order.items || []).reduce((s, it) => s + it.price * it.quantity, 0);

  return (
    <div className="min-h-screen pt-20 md:pt-24 pb-12 bg-[#F1F5F9]" data-testid="order-detail-page">
      {/* Header */}
      <section className="bg-gradient-to-br from-[#2D3E50] to-[#3D5266] text-white py-12 md:py-16 px-6 md:px-12">
        <div className="max-w-5xl mx-auto">
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm mb-5"
            data-testid="order-detail-back"
          >
            <ArrowLeft className="h-4 w-4" />
            Retour à mes commandes
          </Link>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#E8945A] font-medium mb-2">
                <Receipt className="h-4 w-4" />
                Commande
              </span>
              <h1 className="font-['Playfair_Display'] text-3xl sm:text-4xl tracking-tight font-medium">
                {order.invoice_number ? `Facture n° ${order.invoice_number}` : `Commande #${order.id.slice(0, 8).toUpperCase()}`}
              </h1>
              <p className="text-white/60 text-sm mt-2">
                Passée le {format(parseISO(order.created_at), 'dd MMMM yyyy à HH:mm', { locale: fr })}
              </p>
            </div>
            <Badge className={`${m.color} px-4 py-2 text-sm font-medium gap-2`} data-testid="order-detail-status">
              <StatusIcon className="h-4 w-4" />
              {m.label}
            </Badge>
          </div>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-6 md:px-12 py-10 space-y-6">
        {/* Stuck pending_payment alert with manual sync button */}
        {order.status === 'pending_payment' && (
          <div
            className="bg-[#FFFBF5] border-2 border-[#E8945A]/40 rounded-2xl p-5 md:p-6 flex flex-col sm:flex-row sm:items-center gap-4"
            data-testid="order-detail-pending-alert"
          >
            <div className="flex items-start gap-3 flex-1">
              <Clock className="h-5 w-5 text-[#E8945A] flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-[#2D3E50]">
                  En attente de confirmation Mollie
                </p>
                <p className="text-sm text-[#475569] mt-1">
                  Si vous venez de payer, cliquez sur « Vérifier le statut » pour
                  forcer la mise à jour. Si votre paiement a réussi, votre
                  commande sera immédiatement confirmée.
                </p>
              </div>
            </div>
            <Button
              onClick={handleSyncPayment}
              disabled={syncing}
              className="bg-[#E8945A] hover:bg-[#D4844A] gap-2 h-11 flex-shrink-0"
              data-testid="order-detail-sync-payment"
            >
              {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
              Vérifier le statut
            </Button>
          </div>
        )}

        {/* Timeline */}
        {!cancelled && (
          <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6 md:p-8" data-testid="order-timeline">
            <h2 className="font-['Playfair_Display'] text-xl font-medium text-[#2D3E50] mb-6">
              Suivi de votre commande
            </h2>
            <ol className="relative">
              {TIMELINE_STEPS.map((step, idx) => {
                const reached = isStatusReached(step.key, order.status);
                const isCurrent = order.status === step.key;
                const StepIcon = step.icon;
                return (
                  <li
                    key={step.key}
                    className="flex gap-4 pb-6 last:pb-0 relative"
                    data-testid={`timeline-step-${step.key}`}
                  >
                    {idx < TIMELINE_STEPS.length - 1 && (
                      <span
                        className={`absolute left-[19px] top-12 w-0.5 h-[calc(100%-32px)] ${reached ? 'bg-[#E8945A]' : 'bg-[#E2E8F0]'}`}
                      />
                    )}
                    <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center border-2 z-10 ${
                      reached
                        ? 'bg-[#E8945A] border-[#E8945A] text-white'
                        : 'bg-white border-[#E2E8F0] text-[#94A3B8]'
                    }`}>
                      <StepIcon className="h-5 w-5" />
                    </div>
                    <div className="flex-1 pt-1">
                      <div className="flex items-center gap-2">
                        <h3 className={`font-semibold ${reached ? 'text-[#2D3E50]' : 'text-[#94A3B8]'}`}>
                          {step.label}
                        </h3>
                        {isCurrent && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FFF5ED] border border-[#E8945A]/30 text-[10px] uppercase tracking-wider text-[#E8945A] font-medium">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#E8945A] animate-pulse" />
                            En cours
                          </span>
                        )}
                      </div>
                      <p className={`text-sm mt-1 ${reached ? 'text-[#475569]' : 'text-[#94A3B8]'}`}>
                        {step.desc}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>
        )}

        {cancelled && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-6 flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-red-900">{m.label}</p>
              <p className="text-sm text-red-700 mt-1">
                {order.status === 'payment_expired' && 'Le paiement n\'a pas été finalisé dans les délais. Aucun débit n\'a été effectué.'}
                {order.status === 'payment_failed' && 'Le paiement a été refusé. Aucun débit n\'a été effectué. Vous pouvez recommencer une commande.'}
                {order.status === 'payment_canceled' && 'Vous avez annulé le paiement. Aucun débit n\'a été effectué.'}
                {order.status === 'cancelled' && 'Cette commande a été annulée.'}
              </p>
            </div>
          </div>
        )}

        {/* Items */}
        <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6 md:p-8" data-testid="order-detail-items">
          <h2 className="font-['Playfair_Display'] text-xl font-medium text-[#2D3E50] mb-5">
            Détail de la commande
            <span className="ml-2 text-sm text-[#64748B] font-normal">
              ({totalItems} article{totalItems > 1 ? 's' : ''})
            </span>
          </h2>
          <div className="divide-y divide-[#E2E8F0]">
            {(order.items || []).map((it, i) => (
              <div key={i} className="py-3 flex items-center justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-[#2D3E50]">{it.product_name}</div>
                  <div className="text-sm text-[#64748B] mt-0.5">
                    {it.price.toFixed(2)} € × {it.quantity}
                  </div>
                </div>
                <div className="font-semibold text-[#2D3E50] flex-shrink-0">
                  {(it.price * it.quantity).toFixed(2)} €
                </div>
              </div>
            ))}
          </div>

          <div className="mt-5 pt-5 border-t border-[#E2E8F0] space-y-2 text-sm">
            <div className="flex justify-between text-[#64748B]">
              <span>Sous-total articles</span>
              <span>{itemsSubtotal.toFixed(2)} €</span>
            </div>
            {order.delivery_fee > 0 && (
              <div className="flex justify-between text-[#64748B]">
                <span>Frais de livraison express</span>
                <span>{order.delivery_fee.toFixed(2)} €</span>
              </div>
            )}
            {order.booking_fee > 0 && (
              <div className="flex justify-between text-[#64748B]">
                <span>Frais de dossier</span>
                <span>{order.booking_fee.toFixed(2)} €</span>
              </div>
            )}
            <div className="flex justify-between pt-3 mt-2 border-t-2 border-[#2D3E50] text-lg font-bold text-[#2D3E50]">
              <span>Total</span>
              <span className="text-[#E8945A] font-['Playfair_Display']">{order.total_price.toFixed(2)} €</span>
            </div>
          </div>
        </div>

        {/* Addresses + dates */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6">
            <div className="flex items-center gap-2 mb-3">
              <MapPin className="h-4 w-4 text-[#E8945A]" />
              <h3 className="text-xs uppercase tracking-wider text-[#64748B] font-semibold">
                Adresse de livraison
              </h3>
            </div>
            <p className="text-[#2D3E50] leading-relaxed text-sm">
              {order.delivery_address}<br />
              {order.delivery_postal_code} {order.delivery_city}<br />
              {order.delivery_country || 'France'}
            </p>
          </div>

          <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6">
            <div className="flex items-center gap-2 mb-3">
              <Calendar className="h-4 w-4 text-[#E8945A]" />
              <h3 className="text-xs uppercase tracking-wider text-[#64748B] font-semibold">
                Dates de séjour
              </h3>
            </div>
            <div className="text-[#2D3E50] text-sm space-y-1">
              <div>Arrivée : <strong>{format(parseISO(order.arrival_date), 'dd MMMM yyyy', { locale: fr })}</strong></div>
              <div>Départ : <strong>{format(parseISO(order.departure_date), 'dd MMMM yyyy', { locale: fr })}</strong></div>
            </div>
          </div>

          {!order.billing_same_as_delivery && order.billing_address && (
            <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6 md:col-span-2">
              <div className="flex items-center gap-2 mb-3">
                <Receipt className="h-4 w-4 text-[#5493CC]" />
                <h3 className="text-xs uppercase tracking-wider text-[#64748B] font-semibold">
                  Adresse de facturation
                </h3>
              </div>
              <p className="text-[#2D3E50] leading-relaxed text-sm">
                {order.billing_first_name} {order.billing_last_name}<br />
                {order.billing_address}<br />
                {order.billing_postal_code} {order.billing_city}<br />
                {order.billing_country || 'France'}
              </p>
            </div>
          )}
        </div>

        {/* Action bar */}
        <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-start gap-3">
            <Package className="h-5 w-5 text-[#E8945A] flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-[#2D3E50] text-sm">Une question sur cette commande ?</p>
              <a
                href={`mailto:contact@terreetmer-locationdelinge.fr?subject=Commande%20${order.invoice_number || order.id.slice(0,8).toUpperCase()}`}
                className="inline-flex items-center gap-1.5 text-[#E8945A] hover:underline text-sm mt-0.5"
              >
                <Mail className="h-3.5 w-3.5" />
                contact@terreetmer-locationdelinge.fr
              </a>
            </div>
          </div>
          <Button
            onClick={handleDownloadInvoice}
            disabled={downloading || order.status !== 'confirmed' && order.status !== 'in_delivery' && order.status !== 'delivered'}
            className="bg-[#E8945A] hover:bg-[#D4844A] gap-2 h-11"
            data-testid="order-detail-download-invoice"
          >
            {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            Télécharger la facture
          </Button>
        </div>
      </div>
    </div>
  );
};

export default OrderDetailPage;
